import React, { useState, useEffect, useMemo, useCallback } from 'react';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import { useCategories } from '../hooks/useCategories';
import { useLocation, useNavigate } from 'react-router-dom';
import useDataFetch from '../hooks/useDataFetch';
import { getFields, searchFields } from '../utils/fields';
import SearchForm from '../components/SearchForm';
import TableView from '../components/TableView';
import Pagination from '../components/Pagination';
import RelatedMenu from '../components/RelatedMenu';
import CsvUploadButtonProduct from '../components/CsvUploadButtonProduct';
import CsvButtonInventory from '../components/CsvButtonInventory';
import { Box, Stack, IconButton, FormControlLabel, Checkbox, Link, Typography, Paper, Chip, Tooltip } from "@mui/material";
import { Edit } from '@mui/icons-material';
const InventoryPage = ({ hasLimit, getLimit }) => {
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const categories = useCategories();
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState([]);
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await axiosInstance.get('/warehouse', {
          params: { activeOnly: true }
        });
        setWarehouses(res.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error(err);
        }
      }
    };
    fetchWarehouses();
  }, []);
  const {
    dataset,
    error,
    currentPage,
    totalPages,
    searchQuery,
    setSearchQuery,
    handleSearchChange,
    handleSearchSubmit,
    handleClearSearch,
    handlePageChange,
    refetch
  } = useDataFetch('/inventory', {
    keywords: '', category_id: '', is_baseline_stock: false, is_min_stock_level: false
  });
  const groupedDataset = useMemo(() => {
    if (!Array.isArray(dataset)) return [];

    const map = new Map();

    dataset.forEach(row => {
      if (!map.has(row.variant_id)) {
        map.set(row.variant_id, {
          ...row,
          warehouses: []
        });
      }

      map.get(row.variant_id).warehouses.push({
        warehouse_id: row.warehouse_id,
        warehouse_name: row.warehouse_name,
        logical_stock: row.logical_stock,
        baseline_stock: row.baseline_stock,
        min_stock_level: row.min_stock_level
      });
    });

    return Array.from(map.values());
  }, [dataset]);
  useEffect(() => {
    const snackbarState = location.state?.snackbar;
    if (snackbarState) {
      showSnackbar(snackbarState.message, snackbarState.severity);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, showSnackbar]);

  // 行クリック時にページ遷移を行う処理
  const handleRowClick = (item) => {
    navigate(`/products/${item.product_id}`);
  };

  // 検索フィールド定義
  const fieldsToInclude = ['keywords', 'category_id',];
  let searchItems = getFields(searchFields, fieldsToInclude);

  // カラム定義
  const columns = [
    ...(hasLimit('ec') ? [{
      label: '',
      accessor: 'image_url',
      render: (item) => {
        return (
          <img
            alt={item.product_name || '商品画像'}
            src={
              item.image_url
                ? `${process.env.REACT_APP_IMAGE_BASE_URL}${item.image_url.replace(/\\/g, '/')}`
                : `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NOIMAGE}`
            }
            className="product-image"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NO_IMAGE}`;
            }}
          />
        );
      },
      width: '60px',
    }] : []),
    {
      label: '品番',
      accessor: 'sku',
      render: (item) => (
        <Link
          onClick={() => handleRowClick(item)}
        >
          {item.sku}
        </Link>
      ),
      width: '140px',
    },
    {
      label: '商品名',
      accessor: 'product_name',
      width: '350px',
      render: (item) => (
        <Box>
          <Typography>{item.product_name}</Typography>
          <Typography variant='caption' color='text.secondary'>
            {(item.option_combination ?? {}) &&
              Object.entries(item.option_combination).map(([type, value]) => (
                <React.Fragment key={type}>
                  {" "}
                  {type}: {value}
                </React.Fragment>
              ))}
          </Typography>
        </Box>
      )
    },
    ...(hasLimit('ec') ? [{
      label: '公開状況',
      accessor: 'status',
      render: (item) => {
        // product_status が active かつ variant_status が active のときのみ公開
        const isActive = item.product_status === "active" && item.variant_status === "active";
        return (
          <Chip
            label={isActive ? '公開' : '非公開'}
            color={isActive ? 'success' : 'warning'}
          />
        );
      },
      width: '90px',
    }] : []),
    {
      label: '総在庫数',
      accessor: 'total_logical_stock',
      width: '100px'
    },
    {
      label: '倉庫別在庫',
      render: (item) => (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {(item.warehouses ?? []).map(w => {
            let color = "default";
            let tooltip = '';

            if (w.logical_stock < w.min_stock_level) {
              color = "error";
              tooltip = '最小在庫未満';
            } else if (w.logical_stock < w.baseline_stock) {
              color = "warning";
              tooltip = '基準在庫未満';
            }

            return (
              <Tooltip key={w.warehouse_id} title={tooltip} arrow>
                <Chip
                  label={`${w.warehouse_name} ${w.logical_stock}`}
                  size="small"
                  color={color}
                />
              </Tooltip>
            );
          })}
        </Stack>
      ),
      minWidth: '220px'
    },
    {
      render: (item) => (
        <Tooltip title="編集">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleRowClick(item)}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
      width: '48px',
    },
  ];

  const links = [
    { path: '/inventory/transactions/register', label: '入出庫登録', icon: 'sync_alt' },
    { path: '/inventory/transactions', label: '入出庫履歴', icon: 'history' },
    { path: '/products/new', label: '新規商品登録', icon: 'post_add' },
  ];
  useEffect(() => {
    if (searchQuery.warehouse_ids !== undefined) {
      handleSearchSubmit();
    }
  }, [searchQuery.warehouse_ids]);

  if (error) return <GlobalError errorMessage={error} />;
  return (
    <>
      <Typography variant="h1" component="h1">商品在庫一覧</Typography>
      <div className='grid2-2_1'>
        {/* 検索フォーム */}
        <SearchForm
          searchQuery={searchQuery}
          searchFields={searchItems}
          handleSearchChange={handleSearchChange}
          handleSearchSubmit={handleSearchSubmit}
          handleClearSearch={handleClearSearch}
          categories={categories}
        >
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={searchQuery.is_baseline_stock || false}
                  onChange={handleSearchChange}
                  name="is_baseline_stock"
                />
              }
              label="基準在庫未満"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={searchQuery.is_min_stock_level || false}
                  onChange={handleSearchChange}
                  name="is_min_stock_level"
                />
              }
              label="最小在庫未満"
            />
          </Stack>
        </SearchForm>
        <RelatedMenu links={links}>
          {hasLimit('csv_product_upload') && (
            <CsvUploadButtonProduct hasLimit={hasLimit} />
          )}
          {/*getLimit('max_warehouses') > 1 && (
            <CsvButtonInventory />
          )*/
            hasLimit('max_warehouses') && (
              <CsvButtonInventory />
            )
          }
        </RelatedMenu>
      </div>
      <Paper variant='section'>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems='center' justifyContent='end' sx={{ mb: 2 }}>
          <Typography variant="caption">倉庫で絞り込み</Typography>
          {warehouses.map((w) => (
            <Chip
              key={w.warehouse_id}
              label={w.warehouse_name}
              size='medium'
              clickable
              color={warehouseFilter.includes(w.warehouse_id) ? 'primary' : 'default'}
              onClick={() => {
                const warehouseId = w.warehouse_id;
                const newFilter = warehouseFilter.includes(warehouseId)
                  ? warehouseFilter.filter(id => id !== warehouseId)
                  : [...warehouseFilter, warehouseId];

                setWarehouseFilter(newFilter);
                setSearchQuery(prev => ({
                  ...prev,
                  warehouse_ids: newFilter,
                }));
              }}
              sx={{ mb: 1 }}
            />
          ))}
        </Stack>

        {/* テーブル */}
        <TableView dataset={groupedDataset} columns={columns} />

        {/* ページネーション */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </Paper>
    </>
  );
};

export default InventoryPage;
